import React from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, ChevronDown } from "lucide-react";
import Seo from "../components/Seo";
import RetreatInquiryForm from "../components/RetreatInquiryForm";
import { isPrerendered } from "../lib/hydration";
import { BUSINESS, getLocalBusinessSchema } from "../lib/businessInfo";
import { SOCIAL_LINKS } from "../lib/socialLinks";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" },
  }),
};

const faqs = [
  {
    q: "What yoga retreats do you offer in Rishikesh?",
    a: "We list a wide range of yoga retreats in Rishikesh — from weekend getaways to 10–21 day immersive programs including Hatha, Ashtanga, Vinyasa, and Yin yoga in the heart of the yoga capital of the world.",
  },
  {
    q: "Do you offer yoga retreats in Goa?",
    a: "Yes! We have carefully curated yoga retreats in Goa combining beach yoga, sunset meditation, breathwork, and Ayurvedic wellness by the Arabian Sea.",
  },
  {
    q: "What is included in the retreat price?",
    a: "Most retreats include accommodation, daily yoga and meditation sessions, and vegetarian meals. Specific inclusions vary per retreat — you can check the detailed listing on each retreat page.",
  },
  {
    q: "Do you offer yoga teacher training programs?",
    a: "Yes, we list certified 200-hour and 300-hour Yoga Teacher Training programs (YTT) accredited by Yoga Alliance, primarily in Rishikesh and Goa.",
  },
  {
    q: "How do I book a retreat?",
    a: "Browse retreats on our homepage, open a listing, and use the Book Now form to request a reservation. For questions first, use the Enquire option on any retreat page or this contact page.",
  },
  {
    q: "What is your typical response time?",
    a: "We usually respond within 24 hours on business days. For urgent queries, WhatsApp is the fastest way to reach us.",
  },
];

const socialLinks = SOCIAL_LINKS;

const jsonLd = getLocalBusinessSchema({
  url: "https://yogaretreatsbooking.com",
  description:
    "Find and book authentic yoga retreats, meditation retreats, and yoga teacher training programs in India.",
  sameAs: socialLinks.map((s) => s.href),
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    name: BUSINESS.contactName,
    email: BUSINESS.email,
    telephone: BUSINESS.phone,
    availableLanguage: "English",
    areaServed: "Worldwide",
  },
});

function FaqItem({ faq }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
        aria-expanded={open}
      >
        <span className="font-medium text-gray-800 text-sm pr-4">{faq.q}</span>
        <ChevronDown
          size={18}
          className={`text-emerald-600 flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={isPrerendered ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-4 text-sm text-gray-600 leading-relaxed bg-gray-50">
              {faq.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Contact() {
  const [searchParams] = useSearchParams();
  const defaultFormType = searchParams.get("type") === "book" ? "book" : "enquiry";

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Contact Us — Yoga Retreat Inquiries & Bookings"
        description="Enquire about yoga retreats in Rishikesh, Goa, and India, or submit a booking request. Reach us at info@yogaretreatsbooking.com or WhatsApp +91 8789758162."
        canonicalPath="/contact"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative pt-16 overflow-hidden bg-gradient-to-br from-teal-900 via-emerald-800 to-teal-700">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1400&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <motion.div initial={isPrerendered ? false : "hidden"} animate="visible" variants={fadeUp}>
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/30 text-emerald-100 text-sm font-medium mb-4 border border-emerald-400/40">
              We'd love to hear from you
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-5">
              Enquire or <span className="text-emerald-300">Book</span>
            </h1>
            <p className="text-emerald-100/90 text-lg leading-relaxed">
              Ask a question about any retreat, or submit a booking request — we'll respond within 24 hours.
            </p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-white" style={{ clipPath: "ellipse(55% 100% at 50% 100%)" }} />
      </section>

      {/* Form — full width, standard retreat-site layout */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <motion.div
          initial={isPrerendered ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <RetreatInquiryForm defaultFormType={defaultFormType} variant="page" />
        </motion.div>
      </section>

      {/* Contact info + FAQ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid lg:grid-cols-2 gap-14">
          <motion.div
            initial={isPrerendered ? false : "hidden"}
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="flex flex-col gap-10"
          >
            {/* Contact details */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>
              <div className="flex flex-col gap-4">
                <a
                  href={`mailto:${BUSINESS.email}`}
                  className="flex items-center gap-4 p-4 rounded-xl bg-emerald-50 border border-emerald-100 hover:border-emerald-300 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
                    <Mail className="text-emerald-600" size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-0.5">Email</p>
                    <p className="text-gray-800 font-medium text-sm">{BUSINESS.email}</p>
                  </div>
                </a>
                <a
                  href={`https://wa.me/${BUSINESS.phone.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 rounded-xl bg-green-50 border border-green-100 hover:border-green-300 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors">
                    <Phone className="text-green-600" size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-0.5">WhatsApp</p>
                    <p className="text-gray-800 font-medium text-sm">{BUSINESS.phoneDisplay}</p>
                  </div>
                </a>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-sky-50 border border-sky-100">
                  <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-sky-600" size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-0.5">
                      Operational Address
                    </p>
                    <p className="text-gray-800 font-medium text-sm">{BUSINESS.operationalAddress.display}</p>
                  </div>
                </div>
              </div>

              {/* Social profiles */}
              <div className="mt-6">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">Connect with us</p>
                <div className="flex items-center gap-3">
                  {socialLinks.map(({ label, href, Icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      title={label}
                      className="w-11 h-11 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors"
                    >
                      <Icon size={20} />
                    </a>
                  ))}
                </div>
              </div>

              <p className="mt-4 text-sm text-gray-500">
                We usually respond within <strong className="text-gray-700">24 hours</strong>.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={isPrerendered ? false : "hidden"}
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={1}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <div className="flex flex-col gap-3">
              {faqs.map((faq) => (
                <FaqItem key={faq.q} faq={faq} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
